/**
 * MifeLife v2 — Company Analysis (/sirket).
 *
 * 8-question Likert assessment across 4 dimensions (Finance, Marketing, Sales,
 * Operations). Submits to /api/v2/company-analysis/submit, renders 4 score
 * cards + roadmap + mentor CTA. TR / EN / DE.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Sparkles, ArrowRight, ArrowLeft, Loader2, CheckCircle2, TrendingUp, TrendingDown, Share2, Download, Copy, Check, Link2 } from 'lucide-react';
import { toast } from 'sonner';
import V2Nav from '../../components/v2/V2Nav';
import V2Footer from '../../components/v2/V2Footer';
import { apiRequest, getToken, downloadFile } from '../../lib/utils';

// ---------- Copy ----------
const COPY = {
  tr: {
    eyebrow: 'ŞİRKETİNİZ İÇİN',
    h1: 'Şirketinizin sağlık taramasını yapın',
    h1Accent: 'uzman mentör bulun',
    sub: 'Finans, Pazarlama, Satış, Operasyon. 8 soru, 3 dakika. AI şirket profilinizi çıkarır, en zayıf boyutunuzda uzman mentör önerir.',
    startCta: 'Taramayı başlat →',
    free: '%100 ücretsiz · 3 dakika',
    backToHome: '← Ana sayfa',
    likert: ['Hiç değil', 'Kısmen', 'Orta', 'Çoğunlukla', 'Tamamen'],
    company: 'Şirket adı (opsiyonel)',
    industry: 'Sektör (opsiyonel)',
    employees: 'Çalışan sayısı (opsiyonel)',
    questionOf: 'Soru',
    next: 'Sonraki →',
    prev: '← Önceki',
    submit: 'Tarayı tamamla',
    resultTitle: 'Şirket sağlık taraması sonucu',
    strongest: 'En güçlü', weakest: 'En zayıf',
    roadmapTitle: 'AI Yol Haritası — sıradaki 3 adım',
    mentorCta: 'Bu alanda uzman mentörle görüş →',
    dimLabels: { finans: 'Finans', pazarlama: 'Pazarlama', satis: 'Satış', operasyon: 'Operasyon' },
    questions: [
      'Aylık nakit akışımızı düzenli takip edip 3 aylık projeksiyon yapıyoruz',
      'Gelir-gider dengemizi ve kârlılık marjlarımızı analiz ediyoruz',
      'Dijital pazarlama stratejimiz net, ölçülebilir ve ROI takibi yapıyoruz',
      'Marka bilinirliğimizi ve müşteri edinme maliyetimizi (CAC) biliyoruz',
      'Satış hunimiz optimize edilmiş, dönüşüm oranlarını ölçüyoruz',
      'Müşteri yaşam boyu değerini (LTV) hesaplıyor ve artırma stratejisi geliştiriyoruz',
      'İş süreçlerimiz otomatikleştirilmiş, manuel iş yükü minimize',
      'Ekip verimliliğini ölçüyoruz ve sürekli iyileştirme yapıyoruz',
    ],
  },
  en: {
    eyebrow: 'FOR YOUR COMPANY',
    h1: 'Run a health check on your company',
    h1Accent: 'find an expert mentor',
    sub: 'Finance, Marketing, Sales, Operations. 8 questions, 3 minutes. AI maps your company profile and recommends an expert in your weakest area.',
    startCta: 'Start scan →',
    free: '100% free · 3 minutes',
    backToHome: '← Home',
    likert: ['Not at all', 'Partly', 'Average', 'Mostly', 'Fully'],
    company: 'Company name (optional)',
    industry: 'Industry (optional)',
    employees: 'Headcount (optional)',
    questionOf: 'Question',
    next: 'Next →', prev: '← Previous',
    submit: 'Complete scan',
    resultTitle: 'Company health scan result',
    strongest: 'Strongest', weakest: 'Weakest',
    roadmapTitle: 'AI roadmap — next 3 steps',
    mentorCta: 'Talk to an expert in this area →',
    dimLabels: { finans: 'Finance', pazarlama: 'Marketing', satis: 'Sales', operasyon: 'Operations' },
    questions: [
      'We track our monthly cash flow and run 3-month projections',
      'We analyse our revenue, cost balance and profit margins',
      'Our digital marketing strategy is clear, measurable, with ROI tracking',
      'We know our brand awareness and customer acquisition cost (CAC)',
      'Our sales funnel is optimised, we measure conversion rates',
      'We compute customer lifetime value (LTV) and have a strategy to grow it',
      'Our processes are automated, manual workload is minimised',
      'We measure team productivity and continuously improve',
    ],
  },
  de: {
    eyebrow: 'FÜR IHR UNTERNEHMEN',
    h1: 'Machen Sie einen Gesundheitscheck',
    h1Accent: 'finden Sie einen Mentor',
    sub: 'Finanzen, Marketing, Vertrieb, Operationen. 8 Fragen, 3 Minuten. KI erstellt Ihr Unternehmensprofil und schlägt einen Experten für Ihre schwächste Dimension vor.',
    startCta: 'Scan starten →',
    free: '100 % kostenlos · 3 Minuten',
    backToHome: '← Startseite',
    likert: ['Gar nicht', 'Teilweise', 'Mittel', 'Meist', 'Voll'],
    company: 'Unternehmensname (optional)',
    industry: 'Branche (optional)',
    employees: 'Mitarbeiterzahl (optional)',
    questionOf: 'Frage',
    next: 'Weiter →', prev: '← Zurück',
    submit: 'Scan abschließen',
    resultTitle: 'Ergebnis der Unternehmensgesundheits-Analyse',
    strongest: 'Stärkste', weakest: 'Schwächste',
    roadmapTitle: 'KI-Roadmap — nächste 3 Schritte',
    mentorCta: 'Mit einem Experten sprechen →',
    dimLabels: { finans: 'Finanzen', pazarlama: 'Marketing', satis: 'Vertrieb', operasyon: 'Operationen' },
    questions: [
      'Wir verfolgen unseren monatlichen Cashflow und machen 3-Monats-Prognosen',
      'Wir analysieren Einnahmen, Kostenbilanz und Gewinnmargen',
      'Unsere Digital-Marketing-Strategie ist klar, messbar, mit ROI-Tracking',
      'Wir kennen unsere Markenbekanntheit und Customer Acquisition Cost (CAC)',
      'Unser Vertriebstrichter ist optimiert, wir messen Conversion-Raten',
      'Wir berechnen Customer Lifetime Value (LTV) und haben Wachstumsstrategien',
      'Unsere Prozesse sind automatisiert, manuelle Arbeit minimal',
      'Wir messen Team-Produktivität und verbessern kontinuierlich',
    ],
  },
};

// Dimension → color tone (consistent across screens).
const DIM_TONE = {
  finans:     { bg: 'bg-emerald-500/10', border: 'border-emerald-400/30', text: 'text-emerald-300', bar: 'bg-emerald-400' },
  pazarlama:  { bg: 'bg-amber-500/10',   border: 'border-amber-400/30',   text: 'text-amber-300',   bar: 'bg-amber-400' },
  satis:      { bg: 'bg-blue-500/10',    border: 'border-blue-400/30',    text: 'text-blue-300',    bar: 'bg-blue-400' },
  operasyon:  { bg: 'bg-violet-500/10',  border: 'border-violet-400/30',  text: 'text-violet-300',  bar: 'bg-violet-400' },
};

const DIM_TO_SPECIALTY = {
  finans: 'Finans Danışmanı',
  pazarlama: 'Pazarlama Danışmanı',
  satis: 'Satış Koçu',
  operasyon: 'Operasyon Danışmanı',
};

// 0=intro, 1..8=questions, 9=meta (company info), 10=result
const STEP = { INTRO: 0, META: 1, Q_START: 2, RESULT: 11 };

export default function V2CompanyAnalysisPage() {
  const nav = useNavigate();
  const [lang, setLang] = useState('tr');
  const T = COPY[lang] || COPY.tr;
  const [step, setStep] = useState(STEP.INTRO);
  const [answers, setAnswers] = useState(Array(8).fill(null));
  const [meta, setMeta] = useState({ company_name: '', industry: '', employee_count: '' });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    document.title = lang === 'tr' ? 'Şirket Analizi · MifeLife' : (lang === 'en' ? 'Company Analysis · MifeLife' : 'Unternehmens-Analyse · MifeLife');
    document.documentElement.setAttribute('lang', lang);
  }, [lang]);

  const setAnswer = (idx, val) => {
    setAnswers((a) => { const copy = a.slice(); copy[idx] = val; return copy; });
  };

  const goNext = () => setStep((s) => s + 1);
  const goPrev = () => setStep((s) => Math.max(STEP.INTRO, s - 1));

  const submit = async () => {
    setErr(null);
    if (answers.some((a) => a == null)) { setErr(lang === 'en' ? 'Please answer all 8 questions.' : (lang === 'de' ? 'Bitte beantworten Sie alle 8 Fragen.' : 'Lütfen 8 sorunun hepsini cevapla.')); return; }
    setSubmitting(true);
    try {
      // Anonymous users go through guest signup gate. Easiest: store intent and
      // bounce to /kayit.
      if (!getToken()) {
        try { sessionStorage.setItem('v2_company_analysis_intent', JSON.stringify({ answers, meta })); } catch (e) { /* ignore */ }
        nav('/kayit?after=sirket');
        return;
      }
      const res = await apiRequest('/v2/company-analysis/submit', {
        method: 'POST',
        body: JSON.stringify({
          answers,
          company_name: meta.company_name || null,
          industry: meta.industry || null,
          employee_count: meta.employee_count ? parseInt(meta.employee_count, 10) : null,
        }),
      });
      setResult(res);
      setStep(STEP.RESULT);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e2) {
      setErr(e2?.message || 'Tarama gönderilemedi.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0F0F1A] text-white antialiased" data-testid="v2-company-page">
      <V2Nav lang={lang} onLangChange={setLang} />

      <section className="pt-28 sm:pt-32 pb-20">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">

          {step === STEP.INTRO && <Intro T={T} onStart={() => setStep(STEP.META)} />}

          {step === STEP.META && (
            <MetaForm T={T} meta={meta} setMeta={setMeta} onBack={() => setStep(STEP.INTRO)} onNext={goNext} />
          )}

          {step >= STEP.Q_START && step < STEP.RESULT && (
            <Question
              T={T}
              idx={step - STEP.Q_START}
              total={8}
              answer={answers[step - STEP.Q_START]}
              onAnswer={(v) => setAnswer(step - STEP.Q_START, v)}
              onPrev={goPrev}
              onNext={goNext}
              isLast={(step - STEP.Q_START) === 7}
              onSubmit={submit}
              submitting={submitting}
              err={err}
            />
          )}

          {step === STEP.RESULT && result && <Result T={T} result={result} lang={lang} />}
        </div>
      </section>

      <V2Footer lang={lang} />
    </div>
  );
}

// ============================================================================
// SUBVIEWS
// ============================================================================

const Intro = ({ T, onStart }) => (
  <div data-testid="v2-company-intro">
    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-violet-500/10 border border-violet-400/30 text-[11px] uppercase tracking-[0.18em] text-violet-200 mb-6">
      <Sparkles className="w-3 h-3" /> {T.eyebrow}
    </div>
    <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.05]">
      {T.h1}, <span className="bg-gradient-to-r from-violet-300 to-cyan-300 bg-clip-text text-transparent">{T.h1Accent}</span>.
    </h1>
    <p className="mt-5 text-base text-slate-400 leading-relaxed max-w-2xl">{T.sub}</p>

    <div className="mt-8 grid sm:grid-cols-4 gap-3">
      {['finans', 'pazarlama', 'satis', 'operasyon'].map((d) => {
        const t = DIM_TONE[d];
        return (
          <div key={d} className={`rounded-2xl border ${t.border} ${t.bg} p-4 text-center`}>
            <div className={`text-sm font-semibold ${t.text}`}>{T.dimLabels[d]}</div>
          </div>
        );
      })}
    </div>

    <button
      onClick={onStart}
      className="mt-10 inline-flex items-center gap-2 h-12 px-6 rounded-lg bg-gradient-to-r from-violet-500 to-violet-600 text-white text-sm font-semibold hover:from-violet-400 hover:to-violet-500 transition"
      data-testid="v2-company-start"
    >
      {T.startCta} <ArrowRight className="w-4 h-4" />
    </button>
    <div className="mt-3 text-[11px] text-slate-500 uppercase tracking-[0.18em]">{T.free}</div>
  </div>
);

const MetaForm = ({ T, meta, setMeta, onBack, onNext }) => (
  <div data-testid="v2-company-meta">
    <div className="text-[11px] uppercase tracking-[0.18em] text-violet-300 mb-4">0 / 8</div>
    <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight mb-2">Şirketin hakkında</h2>
    <p className="text-sm text-slate-400 mb-8">Hepsi opsiyonel. Atlayıp doğrudan sorulara geçebilirsin.</p>
    <div className="space-y-4">
      <Field label={T.company}>
        <input value={meta.company_name} onChange={(e) => setMeta({ ...meta, company_name: e.target.value })} className={INPUT_CLS} data-testid="v2-company-meta-name" />
      </Field>
      <Field label={T.industry}>
        <input value={meta.industry} onChange={(e) => setMeta({ ...meta, industry: e.target.value })} placeholder="SaaS, perakende, restoran..." className={INPUT_CLS} data-testid="v2-company-meta-industry" />
      </Field>
      <Field label={T.employees}>
        <input type="number" min="0" max="100000" value={meta.employee_count} onChange={(e) => setMeta({ ...meta, employee_count: e.target.value })} className={INPUT_CLS} data-testid="v2-company-meta-headcount" />
      </Field>
    </div>
    <div className="flex gap-3 mt-8">
      <button onClick={onBack} className="h-11 px-5 rounded-lg border border-white/10 bg-white/[0.02] text-sm text-white hover:bg-white/[0.05] transition flex items-center gap-2" data-testid="v2-company-meta-back"><ArrowLeft className="w-4 h-4" /> {T.prev}</button>
      <button onClick={onNext} className="flex-1 h-11 rounded-lg bg-gradient-to-r from-violet-500 to-violet-600 text-white text-sm font-semibold hover:from-violet-400 hover:to-violet-500 transition flex items-center justify-center gap-2" data-testid="v2-company-meta-next">{T.next}</button>
    </div>
  </div>
);

const Question = ({ T, idx, total, answer, onAnswer, onPrev, onNext, isLast, onSubmit, submitting, err }) => (
  <div data-testid={`v2-company-q-${idx}`}>
    <div className="flex items-center justify-between mb-4">
      <div className="text-[11px] uppercase tracking-[0.18em] text-violet-300">{T.questionOf} {idx + 1} / {total}</div>
      <div className="text-xs text-slate-500 tabular-nums">{Math.round(((idx + 1) / total) * 100)}%</div>
    </div>
    {/* Progress bar */}
    <div className="h-1 rounded-full bg-white/[0.06] mb-8 overflow-hidden">
      <div className="h-full bg-gradient-to-r from-violet-500 to-cyan-400 transition-all" style={{ width: `${((idx + 1) / total) * 100}%` }} />
    </div>

    <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight leading-snug mb-8">{T.questions[idx]}</h2>

    <div className="space-y-2.5">
      {T.likert.map((label, i) => {
        const val = i + 1;
        const selected = answer === val;
        return (
          <button
            key={val}
            onClick={() => onAnswer(val)}
            className={`w-full flex items-center justify-between px-4 py-3.5 rounded-xl border text-left transition ${selected ? 'border-violet-400 bg-violet-500/15 text-white' : 'border-white/[0.08] bg-white/[0.02] text-slate-300 hover:border-white/20 hover:bg-white/[0.04]'}`}
            data-testid={`v2-company-q-${idx}-opt-${val}`}
          >
            <span className="text-sm">{label}</span>
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selected ? 'border-violet-400 bg-violet-500' : 'border-white/30'}`}>
              {selected && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
            </div>
          </button>
        );
      })}
    </div>

    {err && <div className="text-sm text-rose-300 mt-4">{err}</div>}

    <div className="flex gap-3 mt-8">
      <button onClick={onPrev} className="h-11 px-5 rounded-lg border border-white/10 bg-white/[0.02] text-sm text-white hover:bg-white/[0.05] transition flex items-center gap-2" data-testid="v2-company-q-prev"><ArrowLeft className="w-4 h-4" /> {T.prev}</button>
      {!isLast ? (
        <button onClick={onNext} disabled={!answer} className="flex-1 h-11 rounded-lg bg-gradient-to-r from-violet-500 to-violet-600 text-white text-sm font-semibold hover:from-violet-400 hover:to-violet-500 disabled:opacity-40 disabled:cursor-not-allowed transition flex items-center justify-center gap-2" data-testid="v2-company-q-next">{T.next}</button>
      ) : (
        <button onClick={onSubmit} disabled={!answer || submitting} className="flex-1 h-11 rounded-lg bg-gradient-to-r from-violet-500 to-violet-600 text-white text-sm font-semibold hover:from-violet-400 hover:to-violet-500 disabled:opacity-40 disabled:cursor-not-allowed transition flex items-center justify-center gap-2" data-testid="v2-company-q-submit">
          {submitting && <Loader2 className="w-4 h-4 animate-spin" />} {T.submit}
        </button>
      )}
    </div>
  </div>
);

const Result = ({ T, result, lang }) => {
  const dims = ['finans', 'pazarlama', 'satis', 'operasyon'];
  return (
    <div data-testid="v2-company-result">
      <div className="text-[11px] uppercase tracking-[0.18em] text-violet-300 mb-3">{T.eyebrow}</div>
      <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight mb-3">{T.resultTitle}</h1>
      {result.company_name && <p className="text-sm text-slate-400 mb-8">{result.company_name}</p>}

      {/* B2B share + PDF */}
      <ShareBar result={result} lang={lang} />

      {/* 4 score cards */}
      <div className="grid sm:grid-cols-2 gap-3 mb-8">
        {dims.map((d) => {
          const t = DIM_TONE[d];
          const score = result.scores?.[d] ?? 0;
          return (
            <div key={d} className={`rounded-2xl border ${t.border} ${t.bg} p-5`} data-testid={`v2-company-score-${d}`}>
              <div className="flex items-baseline justify-between mb-2">
                <span className={`text-sm font-semibold ${t.text}`}>{T.dimLabels[d]}</span>
                <span className="text-2xl font-semibold tabular-nums text-white">%{score}</span>
              </div>
              <div className="h-1.5 rounded-full bg-white/[0.06] overflow-hidden">
                <div className={`h-full ${t.bar} transition-all`} style={{ width: `${score}%` }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Strongest / weakest */}
      <div className="grid sm:grid-cols-2 gap-3 mb-8">
        <Highlight icon={<TrendingUp className="w-4 h-4 text-emerald-300" />} label={T.strongest} value={T.dimLabels[result.strongest_dim]} tone="emerald" testid="v2-company-strongest" />
        <Highlight icon={<TrendingDown className="w-4 h-4 text-rose-300" />} label={T.weakest} value={T.dimLabels[result.weakest_dim]} tone="rose" testid="v2-company-weakest" />
      </div>

      {/* Roadmap */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 sm:p-8 mb-8">
        <h2 className="text-lg sm:text-xl font-semibold mb-5 flex items-center gap-2"><Sparkles className="w-4 h-4 text-violet-300" /> {T.roadmapTitle}</h2>
        <ol className="space-y-3" data-testid="v2-company-roadmap">
          {(result.roadmap_steps || []).map((s, i) => (
            <li key={i} className="flex gap-3 text-sm text-slate-300 leading-relaxed">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-violet-500/15 border border-violet-400/30 text-violet-200 flex items-center justify-center text-xs font-semibold">{i + 1}</span>
              <span>{s}</span>
            </li>
          ))}
        </ol>
      </div>

      {/* Mentor CTA */}
      <Link
        to={`/mentorler?specialty=${encodeURIComponent(DIM_TO_SPECIALTY[result.weakest_dim] || '')}`}
        className="inline-flex items-center gap-2 h-12 px-6 rounded-lg bg-gradient-to-r from-violet-500 to-violet-600 text-white text-sm font-semibold hover:from-violet-400 hover:to-violet-500 transition"
        data-testid="v2-company-mentor-cta"
      >
        {T.mentorCta}
      </Link>
    </div>
  );
};

// ---------- B2B share bar (shareable URL + PDF) ----------
const ShareBar = ({ result, lang }) => {
  const [shareUrl, setShareUrl] = useState(null);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const createLink = async () => {
    setCreating(true);
    try {
      const res = await apiRequest(`/v2/company-analysis/${result.id}/share`, { method: 'POST' });
      setShareUrl(`${window.location.origin}${res.path}`);
      toast.success('Paylaşılabilir link oluşturuldu.');
    } catch (e) {
      toast.error(e?.message || 'Link oluşturulamadı.');
    } finally {
      setCreating(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Link kopyalandı.');
    } catch (e) { /* clipboard blocked */ }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: 'MifeLife Şirket Raporu', url: shareUrl }); } catch (e) { /* cancelled */ }
    } else { copy(); }
  };

  const downloadPdf = async () => {
    setPdfLoading(true);
    try {
      const name = (result.company_name || 'mifelife-sirket-analizi').replace(/\s+/g, '-');
      await downloadFile(`/v2/company-analysis/${result.id}/pdf?lang=${lang}`, `${name}-rapor.pdf`);
    } catch (e) {
      toast.error(e?.message || 'PDF indirilemedi.');
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <div className="rounded-2xl border border-violet-400/20 bg-violet-500/[0.05] p-5 mb-8" data-testid="v2-company-share-bar">
      <div className="flex items-start gap-3 mb-4">
        <Share2 className="w-5 h-5 text-violet-300 shrink-0 mt-0.5" />
        <div>
          <h3 className="text-sm font-semibold text-white">Raporu şirketinle paylaş</h3>
          <p className="text-xs text-slate-400 mt-0.5">Karar vericilere paylaşılabilir link gönder veya profesyonel PDF indir.</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2.5">
        {!shareUrl ? (
          <button onClick={createLink} disabled={creating} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg bg-gradient-to-r from-violet-500 to-violet-600 text-white text-sm font-medium hover:from-violet-400 hover:to-violet-500 disabled:opacity-50 transition" data-testid="v2-company-create-link">
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />} Paylaşılabilir link oluştur
          </button>
        ) : (
          <div className="flex items-center gap-2 flex-1 min-w-[260px] h-10 px-3 rounded-lg bg-[#0a0a14] border border-white/10" data-testid="v2-company-share-url-box">
            <Link2 className="w-3.5 h-3.5 text-slate-500 shrink-0" />
            <input readOnly value={shareUrl} className="flex-1 bg-transparent text-xs text-slate-300 focus:outline-none truncate" data-testid="v2-company-share-url" />
            <button onClick={copy} className="text-slate-400 hover:text-white transition" title="Kopyala" data-testid="v2-company-copy-link">
              {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
        )}
        {shareUrl && (
          <button onClick={nativeShare} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg border border-white/10 bg-white/[0.02] text-sm text-white hover:bg-white/[0.05] transition" data-testid="v2-company-native-share">
            <Share2 className="w-4 h-4" /> Paylaş
          </button>
        )}
        <button onClick={downloadPdf} disabled={pdfLoading} className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg border border-white/10 bg-white/[0.02] text-sm text-white hover:bg-white/[0.05] disabled:opacity-50 transition" data-testid="v2-company-download-pdf">
          {pdfLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} PDF indir
        </button>
      </div>
    </div>
  );
};

const Highlight = ({ icon, label, value, tone, testid }) => (  <div className={`rounded-xl border ${tone === 'emerald' ? 'border-emerald-400/20 bg-emerald-500/[0.04]' : 'border-rose-400/20 bg-rose-500/[0.04]'} p-4 flex items-center gap-3`} data-testid={testid}>
    {icon}
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400">{label}</div>
      <div className="text-base font-semibold text-white">{value}</div>
    </div>
  </div>
);

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[11px] uppercase tracking-[0.18em] text-slate-500 mb-1.5">{label}</span>
    {children}
  </label>
);

const INPUT_CLS = 'w-full h-11 px-3 rounded-lg bg-[#0a0a14] border border-white/10 text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-violet-400/50 transition';
